/**
 * design.md §P4 — the agent side panel.
 *
 * Two visibly distinct reply kinds, per the pattern: answers are prose (they only
 * read, so they are already done), and proposals render a PlanCard (nothing has
 * happened yet). A proposal must never look like a completed action.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  History,
  Lock,
  PanelRightClose,
  Paperclip,
  Sparkles,
  Square,
  SquarePen,
  Wrench,
  X,
} from 'lucide-react'
import * as React from 'react'

import { type DataChange, DataChangeCard } from '@/components/DataChangeCard'
import { Markdown } from '@/components/Markdown'
import { PlanCard } from '@/components/PlanCard'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/form'
import {
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Sheet,
} from '@/components/ui/overlay'
import { Badge } from '@/components/ui/surface'
import { api, ApiError } from '@/lib/api'
import type { FileRef, SchemaChange } from '@/lib/meta'
import { cn } from '@/lib/utils'

export interface AgentContext {
  /** Human-readable breadcrumb of what the user is looking at. */
  label: string
  /** Table key, when the user is on one — the agent is told this. */
  table?: string
  /** The record open on a record page: what "this" means. */
  record?: { id: string; label: string }
  /** What the agent could do here, shown before any conversation. */
  hints?: string[]
}

const AgentContextValue = React.createContext<AgentContext>({ label: 'Home' })

export function AgentContextProvider({
  value,
  children,
}: {
  value: AgentContext
  children: React.ReactNode
}) {
  return <AgentContextValue.Provider value={value}>{children}</AgentContextValue.Provider>
}

export function useAgentContext() {
  return React.useContext(AgentContextValue)
}

// --- rows ticked in a list ----------------------------------------------------------
// A list shares what is ticked, so "send these a WhatsApp" means exactly those rows.

let ticked: { table: string; ids: string[] } = { table: '', ids: [] }
const tickedListeners = new Set<() => void>()

function setTicked(next: typeof ticked) {
  ticked = next
  tickedListeners.forEach((listener) => listener())
}

/** Called by a list with the ids ticked on it (none when "all matching" is chosen:
 *  those are not listed one by one). */
export function useShareSelection(table: string, ids: string[]) {
  const joined = ids.join(',')
  React.useEffect(() => {
    // Only a list with something ticked speaks, so a second list on the page with
    // nothing ticked does not clear the first one's.
    if (!joined) return
    setTicked({ table, ids: joined.split(',') })
    return () => setTicked({ table: '', ids: [] })
  }, [table, joined])
}

function useTicked() {
  return React.useSyncExternalStore(
    (listener) => {
      tickedListeners.add(listener)
      return () => tickedListeners.delete(listener)
    },
    () => ticked,
  )
}

const PANEL_KEY = 'sbt.agent.open'
/** design.md §3: from here the panel sits beside the content; below, it overlays it. */
const WIDE = '(min-width: 1280px)'

function remembered(): boolean {
  try {
    return localStorage.getItem(PANEL_KEY) !== 'false'
  } catch {
    return true
  }
}

function useWide() {
  const [wide, setWide] = React.useState(() => window.matchMedia(WIDE).matches)
  React.useEffect(() => {
    const query = window.matchMedia(WIDE)
    const onChange = () => setWide(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  return wide
}

/** Open or closed, and whether there is room beside the content. On a narrow screen it
 *  always starts closed; only the wide screen's choice is remembered. */
export function useAgentPanel() {
  const wide = useWide()
  const [open, setOpen] = React.useState(() => wide && remembered())
  React.useEffect(() => {
    setOpen(wide && remembered())
  }, [wide])
  const toggle = React.useCallback(() => {
    setOpen((previous) => {
      if (wide) {
        try {
          localStorage.setItem(PANEL_KEY, String(!previous))
        } catch {
          // Remembering is a convenience; the panel still opens.
        }
      }
      return !previous
    })
  }, [wide])
  return { open, toggle, wide }
}

/**
 * design.md §3 / §P4 — the panel on every screen. Wide: beside the content, or a tab
 * when closed. Narrow: always the tab, and opened it slides over the content as a
 * sheet, so nothing is squeezed and the assistant is never out of reach.
 */
/** Opens the assistant from anywhere (the phone's bottom bar): the dock listens. */
export function openAgent() {
  window.dispatchEvent(new Event(OPEN_AGENT))
}

const OPEN_AGENT = 'sbt:open-agent'

export function AgentDock() {
  const { open, toggle, wide } = useAgentPanel()
  React.useEffect(() => {
    const onOpen = () => {
      if (!open) toggle()
    }
    window.addEventListener(OPEN_AGENT, onOpen)
    return () => window.removeEventListener(OPEN_AGENT, onOpen)
  }, [open, toggle])
  if (open && wide) {
    return (
      <div className="shrink-0 print:hidden" style={{ width: 'var(--agent-w)' }}>
        <AgentPanel onClose={toggle} />
      </div>
    )
  }
  return (
    <>
      {/* A tab at the edge; on a phone the bottom bar has the button instead (§3). */}
      <div className="hidden shrink-0 border-l border-border bg-surface md:block print:hidden">
        <Button variant="ghost" size="icon" onClick={toggle} aria-label="Open agent panel" className="m-1">
          <Bot />
        </Button>
      </div>
      {/* A dialog: focus stays inside, Escape and the backdrop close it, focus returns. */}
      <Dialog open={open} onOpenChange={(next) => (next === open ? null : toggle())}>
        <Sheet title="Assistant" className="w-[min(var(--agent-w),100vw)] print:hidden">
          <AgentPanel onClose={toggle} />
        </Sheet>
      </Dialog>
    </>
  )
}

interface AgentMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  added_at: string
  tools: { name: string; is_error: boolean; running?: boolean }[]
  plan: SchemaChange | null
  attachments?: FileRef[]
  data_change?: DataChange | null
  /** Set while the reply is still arriving. */
  streaming?: boolean
  /** How long the reply took and the tokens it used, for replies in this session. */
  seconds?: number
  tokens?: number
}

/** What a tool is doing, in words, while it runs (agents/tools.py). */
const TOOL_LABELS: Record<string, string> = {
  schema_describe: 'Reading the apps',
  schema_plan_change: 'Preparing a change',
  view_update_config: 'Preparing a layout',
  app_history: 'Reading the history',
  app_rollback: 'Preparing a rollback',
  data_query: 'Looking up records',
  data_aggregate: 'Adding up',
  data_propose: 'Preparing records',
  access_propose: 'Preparing access',
  data_import_file: 'Reading the file',
  data_update_where: 'Preparing a change to many',
  integration_actions: 'Checking what can be sent',
  integration_propose: 'Preparing to send',
  action_propose: 'Trying a button on the records',
  change_status: 'Checking a change',
  file_upload: 'Making an upload link',
  memory_read: 'Recalling',
  memory_write: 'Remembering',
}

/** What the assistant reads (agents/runtime.py); the server checks again. */
const FILE_KINDS = [
  {
    label: 'Spreadsheet',
    hint: '.xlsx or .csv: import, update or archive records',
    accept: '.xlsx,.csv',
    icon: FileSpreadsheet,
  },
  { label: 'PDF', hint: 'Read a document, a bill or a form', accept: 'application/pdf', icon: FileText },
  {
    label: 'Image',
    hint: 'A screenshot or photo to read',
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    icon: FileImage,
  },
  { label: 'Text', hint: '.txt, .md or .json', accept: 'text/*,.md,.json', icon: FileCode },
] as const
const MAX_FILES = 5
/** Ticked rows the assistant is told about (agents/runtime.py MAX_CONTEXT_IDS). */
const MAX_TICKED = 200

// --- the conversation -------------------------------------------------------------
// It lives outside the panel: moving between screens mounts a new panel, and the
// thread — even a reply still arriving — carries on. The tab remembers which
// conversation it is on, so a reload reopens it; New chat starts another.

const THREAD_KEY = 'sbt.agent.conversation'

interface Thread {
  id: string | null
  messages: AgentMessage[]
  loaded: boolean
  streaming: boolean
  startedAt: number
  tokens: number
  error: string | null
}

function storedThread(): string | null {
  try {
    return sessionStorage.getItem(THREAD_KEY)
  } catch {
    return null
  }
}

let thread: Thread = {
  id: storedThread(),
  messages: [],
  // A tab with no conversation yet has nothing to open.
  loaded: storedThread() === null,
  streaming: false,
  startedAt: 0,
  tokens: 0,
  error: null,
}
const listeners = new Set<() => void>()
let controller: AbortController | null = null

function setThread(patch: Partial<Thread>) {
  thread = { ...thread, ...patch }
  if ('id' in patch) {
    try {
      if (thread.id) sessionStorage.setItem(THREAD_KEY, thread.id)
      else sessionStorage.removeItem(THREAD_KEY)
    } catch {
      // Remembering is a convenience; the thread still works.
    }
  }
  listeners.forEach((listener) => listener())
}

function setReply(change: (reply: AgentMessage) => AgentMessage) {
  setThread({ messages: thread.messages.map((m) => (m.streaming ? change(m) : m)) })
}

function updateMessage(id: string, patch: Partial<AgentMessage>) {
  setThread({ messages: thread.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)) })
}

function useThread() {
  return React.useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => thread,
  )
}

interface Conversation {
  messages: AgentMessage[]
  /** Still being answered on the server, perhaps since before a reload. */
  running: boolean
}

async function openThread(id: string | null) {
  // Only stops listening: an answer being worked out still finishes and is kept.
  controller?.abort()
  setThread({ id, messages: [], loaded: id === null, error: null, streaming: false })
  if (!id) return
  try {
    const found = await api.get<Conversation>(`/api/agent/conversations/${id}`)
    if (thread.id !== id) return
    setThread({ messages: found.messages, loaded: true })
    if (found.running) void follow(id)
  } catch {
    // Gone or not theirs: start afresh rather than show a broken thread.
    if (thread.id === id) setThread({ id: null, loaded: true })
  }
}

const FOLLOW_EVERY_MS = 2000

/** The answer goes on without us (the tab was reloaded, the connection dropped):
 *  look at the conversation every few seconds until it is there. */
async function follow(id: string) {
  setThread({ streaming: true, startedAt: thread.startedAt || Date.now() })
  for (;;) {
    await new Promise((done) => setTimeout(done, FOLLOW_EVERY_MS))
    if (thread.id !== id) return
    try {
      const found = await api.get<Conversation>(`/api/agent/conversations/${id}`)
      if (thread.id !== id) return
      if (!found.running) {
        setThread({ messages: found.messages, streaming: false })
        return
      }
    } catch {
      // A blip: try again on the next beat.
    }
  }
}

/** Stop: the server ends the answer at its next step and keeps what it said; the
 *  stream (or `follow`) then brings that back. Without a conversation yet, just stop
 *  listening. */
async function stopAnswer() {
  if (!thread.id) {
    controller?.abort()
    return
  }
  try {
    await api.post(`/api/agent/conversations/${thread.id}/stop`)
  } catch {
    controller?.abort()
  }
}

function elapsed() {
  return Math.round((Date.now() - thread.startedAt) / 1000)
}

async function ask(
  text: string,
  attached: FileRef[],
  context: Record<string, unknown>,
  onDone: () => void,
) {
  const now = new Date().toISOString()
  const blank = { added_at: now, tools: [], plan: null }
  setThread({
    streaming: true,
    startedAt: Date.now(),
    tokens: 0,
    error: null,
    messages: [
      ...thread.messages,
      { ...blank, id: `local-${Date.now()}`, role: 'user', content: text, attachments: attached },
      { ...blank, id: 'reply', role: 'assistant', content: '', streaming: true },
    ],
  })
  controller = new AbortController()
  let resume: string | null = null
  try {
    await api.stream(
      '/api/agent/ask',
      {
        message: text,
        attachments: attached.map((f) => f.id),
        conversation_id: thread.id,
        context,
      },
      (event) => {
        switch (event.type) {
          case 'start':
            setThread({ id: event.conversation_id as string, loaded: true })
            break
          case 'text':
            setReply((r) => ({ ...r, content: r.content + (event.text as string) }))
            break
          case 'tool':
            setReply((r) => ({
              ...r,
              tools: [...r.tools, { name: event.name as string, is_error: false, running: true }],
            }))
            break
          case 'tool_done': {
            setReply((r) => {
              const index = r.tools.findIndex((t) => t.running && t.name === event.name)
              const tools = r.tools.map((t, i) =>
                i === index ? { ...t, running: false, is_error: Boolean(event.is_error) } : t,
              )
              return { ...r, tools }
            })
            break
          }
          case 'usage':
            setThread({ tokens: event.tokens as number })
            break
          case 'done':
            setReply(() => ({
              ...(event.message as AgentMessage),
              seconds: elapsed(),
              tokens: event.tokens as number,
            }))
            onDone()
            break
          case 'stopped':
            setReply((r) => ({ ...r, content: `${r.content}\n\n(Stopped.)`.trim() }))
            break
          case 'error':
            setThread({ error: event.message as string })
            break
        }
      },
      controller.signal,
    )
  } catch (err) {
    if (controller.signal.aborted) {
      setReply((r) => ({ ...r, content: `${r.content}\n\n(Stopped.)`.trim() }))
    } else if (thread.id && !(err instanceof ApiError && err.status > 0)) {
      // The connection dropped, not the answer: it is still being worked out.
      resume = thread.id
    } else {
      setThread({ error: err instanceof ApiError ? err.message : 'The assistant could not reply.' })
    }
  } finally {
    // A reply that never arrived leaves no empty bubble behind; one that did keeps
    // what it said and the tools it ran.
    setThread({
      streaming: false,
      messages: thread.messages
        .filter((m) => !m.streaming || m.content || m.tools.length)
        .map((m) =>
          m.streaming
            ? { ...m, streaming: false, seconds: elapsed(), tokens: thread.tokens || undefined }
            : m,
        ),
    })
    controller = null
  }
  if (resume) void follow(resume)
}

function formatTokens(count: number) {
  return count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count)
}

/** Seconds since the reply started, ticking while it runs. */
function useTicker(active: boolean) {
  const [, tick] = React.useState(0)
  React.useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [active])
}

/** One message. Memoised: while a reply streams, only it re-renders — not every
 *  earlier message and its Markdown, thirty times a second. */
const MessageView = React.memo(function MessageView({ message }: { message: AgentMessage }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold tracking-wider text-faint uppercase">
        {message.role === 'user' ? 'You' : 'Agent'}
      </p>
      {message.attachments?.length ? (
        <ul className="flex flex-wrap gap-1">
          {message.attachments.map((file) => (
            <li key={file.id}>
              <a
                href={`/api/files/${file.id}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex max-w-48 items-center gap-1 rounded-md border border-border-soft px-1.5 py-0.5 text-[11px] text-muted hover:text-text"
              >
                <Paperclip className="size-3 shrink-0" />
                <span className="truncate">{file.name}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}

      {message.role === 'user' && message.content ? (
        <p className="rounded-lg bg-accent-soft px-3 py-2 text-[13px] whitespace-pre-wrap text-text">
          {message.content}
        </p>
      ) : null}
      {message.role === 'assistant' && (message.content || message.streaming) ? (
        <div className="rounded-lg bg-surface-2 px-3 py-2 text-[13px] text-text">
          {message.content ? <Markdown text={message.content} /> : null}
          {/* Streaming replies show a caret (design.md §P4). */}
          {message.streaming ? (
            <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-accent align-middle" />
          ) : null}
        </div>
      ) : null}

      {message.tools.some((t) => !t.running) ? (
        <p className="flex flex-wrap items-center gap-1 text-[11px] text-faint">
          <Wrench className="size-3" />
          {message.tools
            .filter((t) => !t.running)
            .map((tool, index) => (
              <span
                key={`${tool.name}-${index}`}
                className={cn('font-mono', tool.is_error && 'text-danger')}
              >
                {tool.name}
              </span>
            ))}
        </p>
      ) : null}

      {message.seconds !== undefined && !message.streaming ? (
        <p className="text-[11px] text-faint">
          {message.seconds}s
          {message.tokens ? ` · ${formatTokens(message.tokens)} tokens` : ''}
        </p>
      ) : null}

      {/* Records it prepared: shown in full, written only on Confirm. */}
      {message.data_change ? (
        <DataChangeCard
          change={message.data_change}
          onSettled={(updated) => updateMessage(message.id, { data_change: updated })}
        />
      ) : null}

      {/* A proposal renders as a PlanCard — identical to the schema screen. */}
      {message.plan ? (
        <PlanCard
          plan={message.plan}
          compact
          onSettled={(updated) => updateMessage(message.id, { plan: updated })}
        />
      ) : null}
    </div>
  )
})

export function AgentPanel({ onClose }: { onClose: () => void }) {
  const context = useAgentContext()
  const queryClient = useQueryClient()
  const current = useThread()
  const tickedNow = useTicked()
  const selected = context.table && tickedNow.table === context.table ? tickedNow.ids : []
  const [draft, setDraft] = React.useState('')
  const [files, setFiles] = React.useState<FileRef[]>([])
  const [uploading, setUploading] = React.useState(false)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const [showHistory, setShowHistory] = React.useState(false)
  const endRef = React.useRef<HTMLDivElement>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)
  useTicker(current.streaming)

  const status = useQuery({
    queryKey: ['agent-status'],
    queryFn: () => api.get<{ enabled: boolean; allowed: boolean }>('/api/agent/status'),
    staleTime: 5 * 60_000,
  })

  const history = useQuery({
    queryKey: ['agent-conversations'],
    queryFn: () =>
      api.get<{ id: string; title: string; context: { label?: string }; changed_at: string }[]>(
        '/api/agent/conversations',
      ),
    enabled: showHistory,
  })

  // A reload, or the first panel this tab shows: reopen the thread it was on.
  React.useEffect(() => {
    if (thread.id && !thread.loaded && !thread.streaming) void openThread(thread.id)
  }, [])

  const reply = current.messages.at(-1)
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [current.messages.length, reply?.content.length, reply?.tools.length])

  function pick(accept: string) {
    if (!fileInput.current) return
    fileInput.current.accept = accept
    fileInput.current.click()
  }

  async function attach(list: FileList | null) {
    if (!list?.length) return
    setUploading(true)
    setUploadError(null)
    try {
      const added: FileRef[] = []
      for (const file of Array.from(list).slice(0, MAX_FILES - files.length)) {
        added.push(await api.upload<FileRef>(file))
      }
      setFiles((existing) => [...existing, ...added])
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'The file could not be added.')
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  function send(override?: string) {
    const text = (override ?? draft).trim()
    if ((!text && files.length === 0) || current.streaming || uploading) return
    setDraft('')
    setFiles([])
    setShowHistory(false)
    const where = {
      label: context.label,
      table: context.table,
      record: context.record,
      selected: selected.length ? selected.slice(0, MAX_TICKED) : undefined,
    }
    // A tool may have written something: read everything on screen again (§P24).
    void ask(text, files, where, () => void queryClient.invalidateQueries())
  }

  // Configured on the server, and given to this person (Administration › Users).
  const configured = status.data?.enabled ?? false
  const allowed = status.data?.allowed ?? false
  const enabled = configured && allowed
  const error = current.error ?? uploadError
  const running = reply?.streaming ? [...reply.tools].reverse().find((t) => t.running) : undefined

  return (
    <aside className="flex h-full flex-col border-l border-border bg-surface" aria-label="Agent">
      <header className="flex items-start justify-between gap-2 border-b border-border-soft px-4 py-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[13px] font-semibold">
            <Bot className="size-4 text-accent" />
            Agent
          </p>
          {/* Mandatory and always accurate (design.md §P4): where the next question is asked. */}
          <p className="mt-0.5 truncate text-[11px] text-muted" title={context.label}>
            Context: {context.label}
            {selected.length ? ` · ${selected.length} ticked` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowHistory((open) => !open)}
            aria-label="Earlier conversations"
            aria-pressed={showHistory}
            title="Earlier conversations"
          >
            <History />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              setShowHistory(false)
              void openThread(null)
            }}
            disabled={current.messages.length === 0 && !current.streaming}
            aria-label="New chat"
            title="New chat"
          >
            <SquarePen />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close agent panel">
            <PanelRightClose />
          </Button>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
        {showHistory ? (
          <div>
            <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">
              Earlier conversations
            </p>
            {history.isPending ? (
              <p className="mt-2 text-xs text-muted">Loading…</p>
            ) : history.isError ? (
              <p role="alert" className="mt-2 text-xs text-danger">
                They could not be loaded.
              </p>
            ) : history.data.length === 0 ? (
              <p className="mt-2 text-xs text-muted">No conversations yet.</p>
            ) : (
              <ul className="mt-2 space-y-0.5">
                {history.data.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        setShowHistory(false)
                        void openThread(item.id)
                      }}
                      className={cn(
                        'w-full rounded-md px-2 py-1.5 text-left hover:bg-surface-2',
                        item.id === current.id && 'bg-accent-soft',
                      )}
                    >
                      <span className="block truncate text-[13px]">{item.title || 'Untitled'}</span>
                      <span className="block truncate text-[11px] text-faint">
                        {item.context?.label ?? 'Home'} · {new Date(item.changed_at).toLocaleString()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {configured && !allowed && status.data ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-2/50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium">
              <Lock className="size-3.5 text-muted" />
              Not given to you
            </p>
            <p className="mt-1 text-xs text-muted">
              The assistant is given person by person. Ask an administrator to switch it on for
              you in Administration › Users.
            </p>
          </div>
        ) : null}
        {!configured ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-2/50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium">
              <Sparkles className="size-3.5 text-accent" />
              Not configured
            </p>
            <p className="mt-1 text-xs text-muted">
              Set <code className="font-mono">ANTHROPIC_API_KEY</code> on the server to enable the
              assistant. Everything else — plans, approvals, workflows — works without it.
            </p>
          </div>
        ) : null}

        {!showHistory && current.messages.length === 0 && current.loaded && context.hints?.length ? (
          <div>
            <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">
              Try asking
            </p>
            <ul className="mt-2 space-y-1.5">
              {context.hints.map((hint) => (
                <li key={hint}>
                  <button
                    onClick={() => setDraft(hint)}
                    disabled={!enabled}
                    className="flex gap-2 text-left text-xs text-muted hover:text-text disabled:cursor-not-allowed"
                  >
                    <span className="text-accent" aria-hidden>
                      ⌁
                    </span>
                    {hint}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!showHistory && !current.loaded && current.id ? (
          <p className="text-xs text-muted">Opening the conversation…</p>
        ) : null}

        {showHistory
          ? null
          : current.messages.map((message) => <MessageView key={message.id} message={message} />)}

        {current.streaming && !showHistory ? (
          <p className="flex items-center gap-2 text-xs text-muted" role="status">
            <Sparkles className="size-3.5 animate-spin text-accent [animation-duration:2.5s]" />
            <span className="min-w-0 truncate">
              {running
                ? `${TOOL_LABELS[running.name] ?? running.name}…`
                : reply?.streaming
                  ? 'Thinking…'
                  : 'Still working…'}
            </span>
            <span className="ml-auto shrink-0 text-faint tabular-nums">
              {elapsed()}s{current.tokens ? ` · ${formatTokens(current.tokens)} tokens` : ''}
            </span>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">
            {error}
          </p>
        ) : null}

        <div ref={endRef} />
      </div>

      <div className="border-t border-border-soft p-3">
        {files.length ? (
          <ul className="mb-2 flex flex-wrap gap-1">
            {files.map((file) => (
              <li
                key={file.id}
                className="inline-flex max-w-52 items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px]"
              >
                <Paperclip className="size-3 shrink-0 text-faint" />
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((existing) => existing.filter((f) => f.id !== file.id))}
                  className="rounded text-faint hover:text-text"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {/* The composer stays usable while a reply arrives; Stop replaces Send (§P4). */}
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
          placeholder={
            enabled
              ? 'Ask or describe a change…'
              : configured
                ? 'Ask an administrator for the assistant'
                : 'Assistant not configured'
          }
          rows={2}
          disabled={!enabled}
          className="min-h-16 text-[13px]"
          aria-label="Message the agent"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <input
              ref={fileInput}
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => void attach(event.target.files)}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={!enabled || files.length >= MAX_FILES}
                  loading={uploading}
                  aria-label="Attach files"
                  title="Attach files (up to 5)"
                >
                  <Paperclip />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-64">
                <DropdownMenuLabel>Attach (up to {MAX_FILES})</DropdownMenuLabel>
                {FILE_KINDS.map((kind) => (
                  <DropdownMenuItem key={kind.label} onSelect={() => pick(kind.accept)}>
                    <kind.icon />
                    <span className="min-w-0">
                      <span className="block text-[13px]">{kind.label}</span>
                      <span className="block text-[11px] text-faint">{kind.hint}</span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge tone="outline" className="truncate font-normal">
              Changes need your approval
            </Badge>
          </div>
          {current.streaming ? (
            <Button size="sm" variant="secondary" onClick={() => void stopAnswer()}>
              <Square />
              Stop
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              disabled={!enabled || uploading || (!draft.trim() && files.length === 0)}
              onClick={() => send()}
            >
              Send
            </Button>
          )}
        </div>
      </div>
    </aside>
  )
}
